import * as Sentry from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';
import { getSentryConfig, beforeSendFilter } from '$lib/monitoring/sentry';

const environment = process.env.NODE_ENV ?? 'development';
const sentryConfig = getSentryConfig(environment);

// Auto-detect release from DigitalOcean or fallback options
const release =
	process.env.SENTRY_RELEASE ||
	process.env.COMMIT_HASH || // DigitalOcean provides this
	process.env.DO_APP_COMMIT_SHA || // Alternative DO variable
	`${process.env.npm_package_name}@${process.env.npm_package_version}` ||
	'unknown';

Sentry.init({
	environment,
	dsn:
		process.env.SENTRY_DSN ??
		'https://ee583c4d608296a71271330b6dfecb4b@o4509525984149504.ingest.us.sentry.io/4509525985656832',
	release,

	// Performance Monitoring
	tracesSampleRate: sentryConfig.tracesSampleRate,
	tracePropagationTargets: sentryConfig.tracePropagationTargets,

	// Session Replay
	replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
	replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,

	// Integrations
	integrations: [
		// Browser tracing for performance monitoring
		Sentry.browserTracingIntegration({
			enableInp: true, // Enable Interaction to Next Paint
			enableLongTask: true, // Track long tasks
		}),
		// Session replay with privacy settings
		Sentry.replayIntegration({
			maskAllText: false, // Don't mask all text by default
			maskAllInputs: true, // Mask input fields for privacy
			blockAllMedia: false, // Allow media replay
			// Mask sensitive selectors
			mask: [
				'[data-sentry-mask]',
				'input[type="password"]',
				'input[type="email"]',
				'input[name*="card"]',
				'input[name*="credit"]',
				'[data-sensitive]',
			],
			// Block specific selectors from replay
			block: ['.stripe-input', '[data-sentry-block]'],
		}),
		// User feedback widget
		Sentry.feedbackIntegration({
			colorScheme: 'system',
			showBranding: false,
			showName: true,
			showEmail: true,
			isNameRequired: false,
			isEmailRequired: true,
			useSentryUser: {
				name: 'username',
				email: 'email',
			},
			autoInject: false, // We'll manually trigger it
			themeDark: {
				background: '#1a1a1a',
				backgroundHover: '#2a2a2a',
				foreground: '#ffffff',
				border: '#333333',
			},
		}),
	],

	// Error filtering
	beforeSend: beforeSendFilter,
	ignoreErrors: sentryConfig.ignoreErrors,
	denyUrls: sentryConfig.denyUrls,

	// Development settings
	debug: sentryConfig.debug,
	enabled: sentryConfig.enabled,

	// Attachments
	maxValueLength: sentryConfig.maxValueLength,
	normalizeDepth: sentryConfig.normalizeDepth,

	// Initial scope configuration
	initialScope: {
		tags: {
			component: 'client',
			runtime: 'browser',
			deploy_platform: 'digitalocean',
		},
		contexts: {
			browser: {
				viewport: {
					width: window.innerWidth,
					height: window.innerHeight,
				},
			},
		},
	},
});

export const handleError: HandleClientError = Sentry.handleErrorWithSentry(
	async ({ error, event }) => {
		console.error('Client-side error:', error);

		// Log the error details for debugging
		const errorDetails = {
			name: error instanceof Error ? error.name : 'Unknown',
			message: error instanceof Error ? error.message : String(error),
			stack: error instanceof Error ? error.stack : undefined,
			path: event.url.pathname,
			url: event.url.toString(),
			userAgent: navigator.userAgent,
			referer: document.referrer,
		};

		// In development, log to console
		if (process.env.NODE_ENV === 'development') {
			console.error('Client error details:', errorDetails);
		}

		// In production, send to server for OpenSearch logging
		if (process.env.NODE_ENV === 'production') {
			try {
				await fetch('/api/log-error', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(errorDetails),
				});
			} catch (err) {
				console.error('Failed to send error to server:', err);
			}
		}

		return {
			message: 'An error occurred',
			code: 'CLIENT_ERROR',
		};
	}
);
